import _ from 'lodash';
import app from '~/app';
import { useState, useEffect, useCallback } from 'react';

export function useIdleMonitor( opts={} ) {
  _.defaults( opts, {
    keyboard    : true,
    mouse       : true,
    touch       : true,
    swipe       : true,
    timeout     : 60,
    interval    : 100,
    onExpired   : () => {},
  } );
  // If less than 1 second, assume the passed time is in seconds...
  if ( opts.timeout < 1000 ) opts.timeout *= 1000;

  const { timeout, interval, onExpired } = opts;

  const [ timeLeft, setTimeLeft ] = useState( timeout );
  const [ expired, setExpired ] = useState( false );
  const [ cancelled, setCancelled ] = useState( false );

  const reset   = useCallback( () => setTimeLeft( timeout ), [ timeout ] );
  const fire    = useCallback( () => setExpired( true ), [] );
  const cancel  = useCallback( () => setCancelled( true ), [] );
  const update  = useCallback( () => {
    setTimeLeft( current => current - opts.interval );
  }, [ opts.interval, setTimeLeft ] );

  useEffect( () => {
    let done = false;
    if ( expired && ! ( done || cancelled ) ) {
      done = true;
      onExpired();
    }
  }, [ expired, onExpired, cancelled ] );

  useEffect( () => {
    if ( timeLeft <= 0 ) setExpired( true );

    const tid = setTimeout( update, interval );
    return () => clearTimeout( tid );
  }, [ timeLeft, update, interval ] );

  const events = useCallback( ( ...args ) => {
    const handler = ( method ) => _.each( args, ev => {
      window[ `${method}EventListener` ]( ev, reset, {
        capture : true,
        passive : true,
      } );
    } );
    handler( 'add' );
    return () => handler( 'remove' );
  }, [ reset ] );

  useEffect( () => {
    if ( ! opts.keyboard ) return;
    return events( 'keydown' );
  }, [ opts.keyboard, events ] );
  useEffect( () => {
    if ( ! opts.mouse ) return;
    return events(
      'mousemove',
      'wheel',
      'DOMMouseScroll',
      'mouseWheel',
      'mousedown',
    );
  }, [ opts.mouse, events ] );
  useEffect( () => {
    if ( ! opts.touch ) return;
    return events(
      'touchstart',
      'touchmove',
      'pointerdown',
      'pointermove',
      'MSPointerDown',
      'MSPointerMove',
    );
  }, [ opts.touch, events ] );

  useEffect( () => {
    if ( ! opts.swipe ) return;
    const svc = app.service( 'swipes' );
    svc.on( 'created', reset );
    return () => svc.off( 'created', reset );
  }, [ opts.swipe, reset ] );

  const seconds = Math.floor( timeLeft / 1000 );
  const percent = ( ( timeLeft / timeout ) * 100 ).toFixed( 2 );
  return {
    reset, fire, cancel, update,
    timeLeft, seconds, expired, cancelled,
    percent,
  };
}
